<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const route = useRoute();
const dialogRef = ref(null);
const isLoading = ref(true);
const hasLicenseError = ref(false);
const isSaving = ref(false);
const isAnalyzing = ref(false);
const selectedInsight = ref(null);
const sortKey = ref('score');
const sortDirection = ref('desc');
const data = ref({ config: {}, insights: [], openai_connected: false });
const form = ref({ enabled: false, window_days: 30 });

const endpoint = computed(
  () => `/api/v1/accounts/${route.params.accountId}/demand_radar`
);
const settingsUrl = computed(
  () => `/app/accounts/${route.params.accountId}/settings/integrations/openai`
);
const isConfigured = computed(
  () => data.value.config.enabled && data.value.openai_connected
);
const categoryLabel = category => {
  if (category === 'request') return t('DEMAND_RADAR.CATEGORIES.REQUEST');
  if (category === 'pain') return t('DEMAND_RADAR.CATEGORIES.PAIN');
  if (category === 'objection') return t('DEMAND_RADAR.CATEGORIES.OBJECTION');
  if (category === 'opportunity')
    return t('DEMAND_RADAR.CATEGORIES.OPPORTUNITY');
  return t('DEMAND_RADAR.CATEGORIES.DEMAND');
};
const growthLabel = insight => {
  const current = Number(insight.current_period_count || 0);
  const previous = Number(insight.previous_period_count || 0);
  if (current > 0 && previous === 0) return t('DEMAND_RADAR.GROWTH.NEW');
  if (current === previous) return t('DEMAND_RADAR.GROWTH.STABLE');
  return t('DEMAND_RADAR.GROWTH.CHANGE', { count: current - previous });
};
const growthTooltip = insight =>
  t('DEMAND_RADAR.GROWTH.TOOLTIP', {
    current: insight.current_period_count || 0,
    previous: insight.previous_period_count || 0,
    total: insight.conversation_count || 0,
  });
const conversationUrl = conversationId =>
  `/app/accounts/${route.params.accountId}/conversations/${conversationId}`;
const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
        new Date(value)
      )
    : '—';
const priorityLabel = score => {
  const value = Number(score);
  if (value >= 70) return t('DEMAND_RADAR.PRIORITY.HIGH');
  if (value >= 40) return t('DEMAND_RADAR.PRIORITY.MEDIUM');
  return t('DEMAND_RADAR.PRIORITY.LOW');
};
const openInsight = insight => {
  selectedInsight.value = insight;
};
const closeInsight = () => {
  selectedInsight.value = null;
};
const sortValue = insight => {
  if (sortKey.value === 'growth') {
    return (
      Number(insight.current_period_count || 0) -
      Number(insight.previous_period_count || 0)
    );
  }
  if (sortKey.value === 'conversations')
    return Number(insight.conversation_count || 0);
  if (sortKey.value === 'last_detected') {
    return new Date(insight.last_detected_at || 0).getTime();
  }
  return Number(insight.score || 0);
};
const sortedInsights = computed(() =>
  [...data.value.insights].sort((a, b) => {
    const difference = sortValue(a) - sortValue(b);
    return sortDirection.value === 'asc' ? difference : -difference;
  })
);
const sortBy = key => {
  if (sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    return;
  }
  sortKey.value = key;
  sortDirection.value = 'desc';
};

const load = async () => {
  isLoading.value = true;
  try {
    const response = await window.axios.get(endpoint.value);
    data.value = response.data;
    form.value = {
      enabled: Boolean(response.data.config.enabled),
      window_days: Number(response.data.config.window_days || 30),
    };
  } catch (error) {
    if (
      error.response?.status === 403 ||
      error.response?.data?.code === 'STACKLAB_TOKEN_NOT_FOUND' ||
      error.data?.code === 'STACKLAB_TOKEN_NOT_FOUND'
    ) {
      hasLicenseError.value = true;
      return;
    }
    useAlert(t('DEMAND_RADAR.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const response = await window.axios.patch(endpoint.value, {
      demand_radar: form.value,
    });
    data.value = response.data;
    dialogRef.value?.close();
    useAlert(t('DEMAND_RADAR.CONFIG.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error === 'openai_not_connected'
        ? t('DEMAND_RADAR.CONFIG.OPENAI_REQUIRED')
        : t('DEMAND_RADAR.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const analyze = async () => {
  isAnalyzing.value = true;
  try {
    const response = await window.axios.post(`${endpoint.value}/analyze`);
    data.value = response.data;
    useAlert(t('DEMAND_RADAR.ANALYZE_QUEUED'));
  } catch {
    useAlert(t('DEMAND_RADAR.ERROR'));
  } finally {
    isAnalyzing.value = false;
  }
};

onMounted(load);
</script>

<template>
  <main
    v-if="!hasLicenseError"
    class="flex-1 overflow-y-auto bg-n-background p-6 lg:p-8"
  >
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <header
        class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
      >
        <div class="flex flex-col gap-1">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ t('DEMAND_RADAR.TITLE') }}
          </h1>
          <p class="max-w-2xl text-sm text-n-slate-11">
            {{ t('DEMAND_RADAR.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex gap-2">
          <Button
            color="slate"
            variant="outline"
            icon="i-lucide-settings-2"
            :label="t('DEMAND_RADAR.CONFIG.ACTION')"
            @click="dialogRef?.open()"
          />
          <Button
            icon="i-lucide-scan-search"
            :label="t('DEMAND_RADAR.ANALYZE')"
            :disabled="!isConfigured"
            :is-loading="isAnalyzing"
            @click="analyze"
          />
        </div>
      </header>

      <div v-if="isLoading" class="flex min-h-64 items-center justify-center">
        <Spinner />
      </div>

      <section
        v-else-if="!isConfigured"
        class="flex min-h-64 flex-col items-center justify-center gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-8 text-center"
      >
        <span class="i-lucide-radar size-10 text-n-brand" />
        <div class="max-w-lg">
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ t('DEMAND_RADAR.EMPTY.CONFIG_TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('DEMAND_RADAR.EMPTY.CONFIG_DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="t('DEMAND_RADAR.CONFIG.ACTION')"
          @click="dialogRef?.open()"
        />
      </section>

      <section
        v-else-if="!data.insights.length"
        class="flex min-h-64 flex-col items-center justify-center gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-8 text-center"
      >
        <span class="i-lucide-search size-10 text-n-slate-10" />
        <div>
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ t('DEMAND_RADAR.EMPTY.INSIGHTS_TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('DEMAND_RADAR.EMPTY.INSIGHTS_DESCRIPTION') }}
          </p>
        </div>
      </section>

      <section
        v-else
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
      >
        <div class="overflow-x-auto">
          <table class="w-full min-w-[980px] text-left text-sm">
            <thead
              class="border-b border-n-weak bg-n-alpha-1 text-xs text-n-slate-11"
            >
              <tr>
                <th class="px-5 py-3 font-medium">
                  {{ t('DEMAND_RADAR.TABLE.DEMAND') }}
                </th>
                <th class="px-4 py-3 font-medium">
                  {{ t('DEMAND_RADAR.TABLE.CATEGORY') }}
                </th>
                <th class="px-4 py-3 font-medium">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-inherit"
                    @click="sortBy('conversations')"
                  >
                    {{ t('DEMAND_RADAR.TABLE.CONVERSATIONS') }}
                    <span
                      v-if="sortKey === 'conversations'"
                      :class="
                        sortDirection === 'asc'
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="size-3"
                    />
                  </button>
                </th>
                <th class="px-4 py-3 font-medium">
                  <button
                    v-tooltip.top="t('DEMAND_RADAR.TABLE.GROWTH_HELP')"
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-inherit"
                    @click="sortBy('growth')"
                  >
                    {{ t('DEMAND_RADAR.TABLE.GROWTH') }}
                    <span
                      v-if="sortKey === 'growth'"
                      :class="
                        sortDirection === 'asc'
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="size-3"
                    />
                  </button>
                </th>
                <th class="px-4 py-3 font-medium">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-inherit"
                    @click="sortBy('score')"
                  >
                    {{ t('DEMAND_RADAR.TABLE.PRIORITY') }}
                    <span
                      v-if="sortKey === 'score'"
                      :class="
                        sortDirection === 'asc'
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="size-3"
                    />
                  </button>
                </th>
                <th class="px-4 py-3 font-medium">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 text-xs font-medium text-inherit"
                    @click="sortBy('last_detected')"
                  >
                    {{ t('DEMAND_RADAR.TABLE.LAST_DETECTED') }}
                    <span
                      v-if="sortKey === 'last_detected'"
                      :class="
                        sortDirection === 'asc'
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="size-3"
                    />
                  </button>
                </th>
                <th class="px-5 py-3 text-right font-medium">
                  {{ t('DEMAND_RADAR.TABLE.EVIDENCE') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="insight in sortedInsights"
                :key="insight.id"
                class="cursor-pointer align-top transition-colors hover:bg-n-alpha-1"
                @click="openInsight(insight)"
              >
                <td class="max-w-[28rem] px-5 py-4">
                  <p class="font-medium text-n-slate-12">{{ insight.title }}</p>
                </td>
                <td class="whitespace-nowrap px-4 py-4 text-n-slate-11">
                  {{ categoryLabel(insight.category) }}
                </td>
                <td
                  class="whitespace-nowrap px-4 py-4 font-medium text-n-slate-12"
                >
                  {{ insight.conversation_count }}
                </td>
                <td
                  v-tooltip="growthTooltip(insight)"
                  class="whitespace-nowrap px-4 py-4 font-medium"
                  :class="
                    Number(insight.current_period_count) >
                    Number(insight.previous_period_count)
                      ? 'text-n-teal-11'
                      : 'text-n-slate-11'
                  "
                >
                  {{ growthLabel(insight) }}
                </td>
                <td class="whitespace-nowrap px-4 py-4">
                  <span class="font-medium text-n-slate-12">{{
                    Math.round(Number(insight.score))
                  }}</span>
                  <span class="ml-1 text-xs text-n-slate-11">{{
                    priorityLabel(insight.score)
                  }}</span>
                </td>
                <td class="whitespace-nowrap px-4 py-4 text-n-slate-11">
                  {{ formatDate(insight.last_detected_at) }}
                </td>
                <td class="px-5 py-4 text-right">
                  <span class="text-xs font-medium text-n-blue-11">
                    {{
                      t('DEMAND_RADAR.EVIDENCE.COUNT', {
                        count: insight.evidence?.length || 0,
                      })
                    }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div
      v-if="selectedInsight"
      class="fixed inset-0 z-40 bg-black/20"
      @click="closeInsight"
    >
      <aside
        class="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col border-l border-n-weak bg-n-solid-2 shadow-xl"
        role="dialog"
        aria-modal="true"
        @click.stop
      >
        <header
          class="flex items-start justify-between border-b border-n-weak px-6 py-5"
        >
          <div class="min-w-0 pr-4">
            <p class="text-xs font-medium uppercase tracking-wide text-n-brand">
              {{ categoryLabel(selectedInsight.category) }}
            </p>
            <h2 class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ selectedInsight.title }}
            </h2>
          </div>
          <Button
            color="slate"
            variant="ghost"
            icon="i-lucide-x"
            :aria-label="t('DEMAND_RADAR.DETAILS.CLOSE')"
            @click="closeInsight"
          />
        </header>

        <div class="flex-1 overflow-y-auto px-6 py-5">
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs text-n-slate-11">
                {{ t('DEMAND_RADAR.TABLE.CONVERSATIONS') }}
              </p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ selectedInsight.conversation_count }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs text-n-slate-11">
                {{ t('DEMAND_RADAR.TABLE.GROWTH') }}
              </p>
              <p class="mt-1 text-lg font-semibold text-n-teal-11">
                {{ growthLabel(selectedInsight) }}
              </p>
              <p class="mt-1 flex flex-wrap gap-1 text-xs text-n-slate-11">
                {{ selectedInsight.current_period_count }}
                {{ t('DEMAND_RADAR.GROWTH.CURRENT') }}
                {{ selectedInsight.previous_period_count }}
                {{ t('DEMAND_RADAR.GROWTH.PREVIOUS') }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs text-n-slate-11">
                {{ t('DEMAND_RADAR.TABLE.PRIORITY') }}
              </p>
              <p class="mt-1 text-lg font-semibold text-n-slate-12">
                {{ Math.round(Number(selectedInsight.score)) }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ priorityLabel(selectedInsight.score) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs text-n-slate-11">
                {{ t('DEMAND_RADAR.TABLE.LAST_DETECTED') }}
              </p>
              <p class="mt-1 text-sm font-medium text-n-slate-12">
                {{ formatDate(selectedInsight.last_detected_at) }}
              </p>
            </div>
          </div>

          <section class="mt-6">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('DEMAND_RADAR.DETAILS.SUMMARY') }}
            </h3>
            <p class="mt-2 text-sm leading-6 text-n-slate-11">
              {{ selectedInsight.summary }}
            </p>
          </section>

          <section v-if="selectedInsight.evidence?.length" class="mt-6">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('DEMAND_RADAR.EVIDENCE.TITLE') }}
            </h3>
            <div class="mt-3 flex flex-col gap-3">
              <a
                v-for="evidence in selectedInsight.evidence"
                :key="evidence.conversation_id"
                :href="conversationUrl(evidence.conversation_id)"
                class="rounded-lg border border-n-weak p-4 hover:border-n-brand/50 hover:bg-n-alpha-1"
              >
                <span class="text-xs font-medium text-n-blue-11">
                  {{
                    t('DEMAND_RADAR.EVIDENCE.CONVERSATION', {
                      id: evidence.conversation_id,
                    })
                  }}
                </span>
                <span class="mt-2 block text-sm leading-5 text-n-slate-11">{{
                  evidence.excerpt
                }}</span>
              </a>
            </div>
          </section>
        </div>
      </aside>
    </div>

    <Dialog
      ref="dialogRef"
      :title="t('DEMAND_RADAR.CONFIG.TITLE')"
      :description="t('DEMAND_RADAR.CONFIG.DESCRIPTION')"
      :confirm-button-label="t('DEMAND_RADAR.CONFIG.SAVE')"
      :is-loading="isSaving"
      @confirm="save"
    >
      <div class="flex flex-col gap-5">
        <div
          class="flex items-center justify-between gap-4 rounded-lg border border-n-weak p-4"
        >
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('DEMAND_RADAR.CONFIG.ENABLED') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('DEMAND_RADAR.CONFIG.ENABLED_HELP') }}
            </p>
          </div>
          <Switch v-model="form.enabled" />
        </div>
        <Input
          v-model="form.window_days"
          type="number"
          min="7"
          max="90"
          :label="t('DEMAND_RADAR.CONFIG.WINDOW')"
          :message="t('DEMAND_RADAR.CONFIG.WINDOW_HELP')"
        />
        <div class="rounded-lg bg-n-alpha-2 p-4 text-sm text-n-slate-11">
          <p class="flex items-center gap-2">
            <span
              v-if="data.openai_connected"
              class="i-lucide-circle-check size-4 shrink-0 text-n-teal-11"
              aria-hidden="true"
            />
            {{
              data.openai_connected
                ? t('DEMAND_RADAR.CONFIG.OPENAI_CONNECTED')
                : t('DEMAND_RADAR.CONFIG.OPENAI_REQUIRED')
            }}
          </p>
          <a
            :href="settingsUrl"
            class="mt-2 inline-block text-n-blue-11 hover:underline"
          >
            {{ t('DEMAND_RADAR.CONFIG.OPENAI_ACTION') }}
          </a>
        </div>
      </div>
    </Dialog>
  </main>

  <div
    v-else
    class="flex h-full w-full items-center justify-center bg-n-surface-2"
  >
    <div class="mx-auto max-w-md p-6 text-center">
      <span class="i-lucide-lock mb-4 inline-block size-12 text-n-slate-10" />
      <h2 class="mb-2 text-xl font-semibold text-n-slate-12">
        {{ t('DEMAND_RADAR.LICENSE_REQUIRED') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('DEMAND_RADAR.LICENSE_DESCRIPTION') }}
      </p>
    </div>
  </div>
</template>
